import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useState } from "react";
import { X } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import CanonicalTag from "@/components/CanonicalTag";

const ADUAsRentalProperty = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <CanonicalTag url="https://www.udaadu.com/adu-as-rental-property" />
      <Navigation />
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-background to-muted py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-8 text-[hsl(var(--brand-primary))]">
            Turn Your ADU into a Monthly Income Stream
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-12">
            Renting out your ADU is one of the smartest ways to turn your property into a long-term income generator. Whether you're listing on Airbnb or securing a full-time tenant, we'll help you design and build an ADU that's optimized for comfort, privacy, and profitability.
          </p>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-12">
            From studio units to full one-bed layouts, our designs focus on maximizing livable square footage while staying compliant with local rental laws. Privacy partitions, smart storage, separate entrances, and durable finishes make your unit attractive to renters and easy to maintain.
          </p>
        </div>
      </section>

      {/* ADU Photos Section */}
      <section className="py-16 px-4 bg-muted/50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <img 
                  src="/lovable-uploads/bc636e24-1e40-4c46-9ace-fc6efc3e545c.png" 
                  alt="Modern Minimalist ADU"
                  className="h-48 w-full object-cover"
                />
              </CardContent>
            </Card>
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <img 
                  src="/lovable-uploads/75108e2e-3bad-4e91-8f94-52420907c290.png" 
                  alt="Modern Black ADU"
                  className="h-48 w-full object-cover"
                />
              </CardContent>
            </Card>
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <img 
                  src="/lovable-uploads/508785b0-ff40-4f19-8105-53b1e178e877.png" 
                  alt="Blue ADU with Open Doors"
                  className="h-48 w-full object-cover"
                />
              </CardContent>
            </Card>
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <img 
                  src="/lovable-uploads/adea23ce-4a81-411d-a25e-38a555745f2a.png" 
                  alt="Farmhouse Style ADU"
                  className="h-48 w-full object-cover"
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* What You'll Learn Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-[hsl(var(--brand-primary))]">
            What You'll Learn on This Page
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">How to design an ADU tenants love</h3>
                <p className="text-muted-foreground">
                  Learn the key design elements that make ADUs attractive to quality tenants and maximize occupancy rates.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">What features help maximize rental income</h3>
                <p className="text-muted-foreground">
                  Discover which amenities and design choices provide the best return on investment for rental properties.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">How much ROI you can expect</h3>
                <p className="text-muted-foreground">
                  Get realistic projections based on your location and ADU size to make informed investment decisions.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Short-term vs long-term rental options</h3>
                <p className="text-muted-foreground">
                  Compare the pros and cons of Airbnb versus traditional leasing to choose the best strategy for you.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Call-to-Action Section */}
      <section className="py-16 px-4 bg-[hsl(var(--brand-primary))] text-[hsl(var(--brand-primary-foreground))]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-8">Ready to Start Your ADU Rental Journey?</h2>
          <p className="text-lg mb-12 opacity-90">
            Let's help you create the perfect ADU that generates consistent rental income.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Button 
              onClick={openCalendlyModal}
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90 w-full"
            >
              Book & Schedule ➝
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90"
            >
              <a 
                href="https://www.udaadu.com/landing" 
                className="w-full"
              >
                What fits my lot? ➝
              </a>
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90"
            >
              <a 
                href="https://www.udaadu.com/adu-calculator" 
                className="w-full"
              >
                Get your price and ROI report ➝
              </a>
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90"
            >
              <a 
                href="https://www.udaadu.com/our-work" 
                className="w-full"
              >
                Our Portfolio ➝
              </a>
            </Button>
            <Button 
              asChild 
              variant="secondary" 
              size="lg" 
              className="bg-white text-[hsl(var(--brand-primary))] hover:bg-white/90 md:col-span-2 lg:col-span-1"
            >
              <a 
                href="https://www.udaadu.com/faq" 
                className="w-full"
              >
                Our FAQ ➝
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button 
              onClick={closeCalendlyModal} 
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
            >
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe 
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" 
              width="100%" 
              height="100%" 
              frameBorder="0" 
              title="Schedule a meeting" 
            />
          </div>
        </div>
      )}
      <Footer />
      <FloatingADUWizard />
    </div>
  );
};

export default ADUAsRentalProperty;